import React, { useState, useEffect, useRef } from 'react';
import { Search, Loader } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';

interface AutocompleteOption {
  id: string;
  label: string;
  [key: string]: any;
}

interface AutocompleteProps {
  value: string;
  onChange: (value: string, selectedOption?: AutocompleteOption) => void;
  onSearch: (query: string) => Promise<AutocompleteOption[]>;
  placeholder?: string;
  label?: string;
  required?: boolean;
  minChars?: number; // Número mínimo de caracteres para buscar (padrão: 3). Use 0 para permitir busca vazia
  getDisplayValue?: (option: AutocompleteOption) => string;
  style?: React.CSSProperties;
  onCreateNew?: () => void; // Callback para criar novo item quando não encontrar resultados
  showCreateOption?: boolean; // Se deve mostrar opção de criar novo
  loadOnFocus?: boolean; // Se deve carregar opções iniciais quando o campo recebe foco (busca com string vazia)
}

const Autocomplete: React.FC<AutocompleteProps> = ({
  value,
  onChange,
  onSearch,
  placeholder,
  label,
  required = false,
  minChars = 3,
  getDisplayValue = (option) => option.label,
  style,
  onCreateNew,
  showCreateOption = false,
  loadOnFocus = false
}) => {
  const { t } = useI18n();
  const defaultPlaceholder = placeholder || t('autocomplete.placeholder');
  const [options, setOptions] = useState<AutocompleteOption[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Buscar opções quando o usuário digita
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    onChange(query);

    // Limpar timer anterior
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Se tiver menos que minChars e minChars > 0, não buscar
    if (minChars > 0 && query.length < minChars) {
      setOptions([]);
      setIsOpen(false);
      return;
    }

    // Debounce de 300ms
    setIsLoading(true);
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const results = await onSearch(query);
        setOptions(results);
        // Se não encontrou resultados mas tem showCreateOption e onCreateNew, mostrar opção de criar
        const shouldShowCreate = Boolean(showCreateOption && (minChars === 0 || query.length >= minChars) && onCreateNew);
        setIsOpen(results.length > 0 || shouldShowCreate);
        setIsLoading(false);
      } catch (error) {
        console.error('Erro ao buscar:', error);
        setOptions([]);
        // Mesmo com erro, mostrar opção de criar se configurado
        const shouldShowCreate = Boolean(showCreateOption && (minChars === 0 || query.length >= minChars) && onCreateNew);
        setIsOpen(shouldShowCreate);
        setIsLoading(false);
      }
    }, 300);
  };

  const handleSelect = (option: AutocompleteOption) => {
    onChange(getDisplayValue(option), option);
    setIsOpen(false);
    setOptions([]);
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || options.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < options.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          handleSelect(options[highlightedIndex]);
        } else if (options.length === 1) {
          handleSelect(options[0]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', ...style }}>
      {label && (
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
          {label} {required && <span style={{ color: 'red' }}>*</span>}
        </label>
      )}
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={async () => {
            // Se loadOnFocus está ativo e não há opções carregadas, buscar opções iniciais
            if (loadOnFocus && options.length === 0 && !isLoading) {
              setIsLoading(true);
              try {
                const results = await onSearch('');
                setOptions(results);
                setIsOpen(results.length > 0 || showCreateOption);
                setIsLoading(false);
              } catch (error) {
                console.error('Erro ao carregar opções iniciais:', error);
                setOptions([]);
                setIsOpen(showCreateOption);
                setIsLoading(false);
              }
            } else if (options.length > 0 && (minChars === 0 || value.length >= minChars)) {
              setIsOpen(true);
            }
          }}
          onKeyDown={handleKeyDown}
          placeholder={defaultPlaceholder}
          required={required}
          style={{
            width: '100%',
            padding: '8px 35px 8px 12px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            outline: 'none',
            fontSize: '14px'
          }}
        />
        <div style={{
          position: 'absolute',
          right: '8px',
          top: '50%',
          transform: 'translateY(-50%)',
          pointerEvents: 'none'
        }}>
          {isLoading ? (
            <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
          ) : (
            <Search size={16} color="#999" />
          )}
        </div>
        {value.length > 0 && value.length < minChars && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: '4px',
            fontSize: '12px',
            color: '#666',
            fontStyle: 'italic'
          }}>
            {t('autocomplete.minChars', { minChars })}
          </div>
        )}
      </div>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          marginTop: '4px',
          backgroundColor: 'white',
          border: '1px solid #ddd',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          maxHeight: '200px',
          overflowY: 'auto',
          zIndex: 1000
        }}>
          {options.length > 0 ? (
            <>
              {options.map((option, index) => (
                <div
                  key={option.id}
                  onClick={() => handleSelect(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  style={{
                    padding: '10px 12px',
                    cursor: 'pointer',
                    backgroundColor: highlightedIndex === index ? '#f3f4f6' : 'white',
                    borderBottom: index < options.length - 1 ? '1px solid #f0f0f0' : 'none'
                  }}
                >
                  <div style={{ fontWeight: '500', color: '#333' }}>
                    {getDisplayValue(option)}
                  </div>
                  {option.address && (
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                      {option.address}
                    </div>
                  )}
                </div>
              ))}
              {/* Mostrar opção de criar mesmo quando há resultados */}
              {showCreateOption && value.length >= minChars && onCreateNew && (
                <div
                  onClick={() => {
                    onCreateNew();
                    setIsOpen(false);
                  }}
                  style={{
                    padding: '12px',
                    cursor: 'pointer',
                    backgroundColor: '#f0f9ff',
                    borderTop: '2px solid #e5e7eb',
                    textAlign: 'center',
                    color: '#9333ea',
                    fontWeight: '500',
                    fontSize: '14px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#e0e7ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#f0f9ff';
                  }}
                >
                  <span style={{ marginRight: '6px' }}>✨</span>
                  {t('autocomplete.createNew', { value })}
                </div>
              )}
            </>
          ) : (
            showCreateOption && value.length >= minChars && onCreateNew && (
              <div
                onClick={() => {
                  onCreateNew();
                  setIsOpen(false);
                }}
                style={{
                  padding: '12px',
                  cursor: 'pointer',
                  backgroundColor: '#f0f9ff',
                  textAlign: 'center',
                  color: '#9333ea',
                  fontWeight: '500',
                  fontSize: '14px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#e0e7ff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f0f9ff';
                }}
              >
                <span style={{ marginRight: '6px' }}>✨</span>
                Criar novo: "{value}"
              </div>
            )
          )}
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: translateY(-50%) rotate(0deg); }
          to { transform: translateY(-50%) rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default Autocomplete;

